import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styles from "../Styles/Dashboard.module.css";
import { FaAngleDown, FaAngleRight } from "react-icons/fa";
import { Navbar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPowerOff,
  faRightLeft,
  faCompress,
  faExpand,
  faBars,
  faBookOpen,
  faComments,
  faUser,
  faArrowRightFromBracket
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import Cookies from "js-cookie";
import gearsGif from "../Assets/gears.gif";

const ICON_MAP = {
  bookOpen: faBookOpen,
  comments: faComments,
  rightLeft: faRightLeft,
  powerOff: faPowerOff,
};

const normalizeName = (name = "") =>
  name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[-_]/g, "");

function Dashboard() {


  const [openDropdown, setOpenDropdown] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 767);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const activeKcSubCategoryId = searchParams.get("subCategoryId");
  const activeContext = searchParams.get("context");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const profileRef = useRef(null);

  const [kcSubCategories, setKcSubCategories] = useState([]);
  const [kcMainCategoryId, setKcMainCategoryId] = useState("");
  const [forumSubCategories, setForumSubCategories] = useState([]);
  const [forumMainCategoryId, setForumMainCategoryId] = useState("");
  const [sidebarError, setSidebarError] = useState("");
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [menuSections, setMenuSections] = useState([]);

  const toggleDropdown = (menuId) => {
    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
    }
    setOpenDropdown(openDropdown === menuId ? null : menuId);
  };

  useEffect(() => {
    let matchedDropdown = null;

    menuSections.forEach((section) => {
      (section.items || []).forEach((item) => {
        const dropdown = Array.isArray(item.dropdown) ? item.dropdown : [];
        if (dropdown.length === 0) return;
        const hasStaticMatch = dropdown.some((dropdownItem) => location.pathname === dropdownItem.link);
        if (hasStaticMatch) matchedDropdown = item.id;
      });
    });

    const isKcRoute =
      location.pathname.startsWith("/admin/topics") ||
      location.pathname.startsWith("/admin/topic-entries") ||
      location.pathname.startsWith("/admin/community-chat");

    if (!matchedDropdown && isKcRoute && activeKcSubCategoryId) {
      const targetId = activeContext === "forum" ? "community_forum" : "knowledge_center";
      const exists = menuSections.some((section) =>
        (section.items || []).some((item) => item.id === targetId)
      );
      if (exists) matchedDropdown = targetId;
    }

    if (matchedDropdown) {
      setOpenDropdown(matchedDropdown);
    }
  }, [location.pathname, activeKcSubCategoryId, activeContext, menuSections]);


  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 767) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);



  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.exitFullscreen) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileOpen((prev) => !prev);
  };



  const getAdminAuthHeaders = useCallback(() => {
    const token = Cookies.get("adminToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const getSidebarData = useCallback(async () => {
    try {
      setSidebarLoading(true);
      setSidebarError("");
      const mainRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/admin/main-categories`,
        { headers: getAdminAuthHeaders() }
      );
      const mainCategories = mainRes.data?.data || [];

      const knowledgeCenterMain =
        mainCategories.find((m) => normalizeName(m?.name) === "knowledge center") ||
        mainCategories.find((m) => normalizeName(m?.name) === "Knowledge Center") ||
        mainCategories.find((m) => normalizeName(m?.name) === "Knowledge center") || null;
      const communityForumMain =
        mainCategories.find((m) => normalizeName(m?.name) === "community forum") ||
        mainCategories.find((m) => normalizeName(m?.name) === "Community Forum") ||
        mainCategories.find((m) => normalizeName(m?.name) === "Community forum") ||
        null;

      const kcId = knowledgeCenterMain?._id || "";
      const forumId = communityForumMain?._id || "";

      const [kcSubsRes, forumSubsRes] = await Promise.all([
        kcId
          ? axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/admin/sub-categories/${kcId}`,
            { headers: getAdminAuthHeaders() }
          )
          : Promise.resolve(null),
        forumId
          ? axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/admin/sub-categories/${forumId}`,
            { headers: getAdminAuthHeaders() }
          )
          : Promise.resolve(null),
      ]);

      const kcSubs = kcSubsRes?.data?.data || [];
      const forumSubs = forumSubsRes?.data?.data || [];

      setKcMainCategoryId(kcId);
      setKcSubCategories(kcSubs);
      setForumMainCategoryId(forumId);
      setForumSubCategories(forumSubs);

      const screens = [];
      if (kcId && kcSubs.length > 0) {
        screens.push({ id: "knowledge_center", label: "Knowledge Center", icon: "bookOpen", type: "kc" });
      }
      if (forumId && forumSubs.length > 0) {
        screens.push({ id: "community_forum", label: "Community Forum", icon: "comments", type: "forum" });
      }

      const menu = [
        { title: "Screens", items: screens },
        {
          title: "Account",
          items: [
            { id: "change_password", label: "Change Password", icon: "rightLeft", link: "/admin/password" },
            { id: "logout", label: "Logout", icon: "powerOff", link: "/admin/logout" },
          ],
        },
      ].filter((section) => (section.items || []).length > 0);

      setMenuSections(menu);
    } catch (error) {
      console.error("Sidebar data fetch error:", error);
      setSidebarError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to load sidebar data"
      );
    } finally {
      setSidebarLoading(false);
    }
  }, [getAdminAuthHeaders]);

  useEffect(() => {
    getSidebarData();
  }, [getSidebarData]);

  return (
    <>
      <header className={styles.header}>
        <div className={styles.nav}>
          <Navbar expand="md" bg="white" fixed="top">
            <button
              className={styles.hamburger_btn}
              onClick={toggleSidebar}
              aria-label="Toggle sidebar"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <div
              className={`${styles.company_logo_container} ${isSidebarOpen ? styles.logo_open : styles.logo_closed
                }`}
            >
              <h1 className={styles.company_logo}>VroPay</h1>
              {/* <img
                src={Logo}
                alt="Company Logo"
                className={styles.company_logo}
              /> */}
            </div>

            <div className={styles.nav_right}>
              <div className={styles.nav_left_group}>
                <div className={styles.nav_actions}>
                  <button
                    className={styles.desktop_menu_btn}
                    onClick={toggleSidebar}
                    aria-label="Toggle sidebar"
                  >
                    <FontAwesomeIcon icon={faBars} />
                  </button>
                  <button
                    onClick={toggleFullScreen}
                    className={styles.fullscreen_toggle_btn}
                    aria-label="Toggle fullscreen"
                  >
                    <FontAwesomeIcon
                      icon={isFullscreen ? faCompress : faExpand}
                    />
                  </button>
                </div>
              </div>

              <div className={styles.nav_user_wrapper} ref={profileRef}>
                <div
                  className={styles.nav_user}
                  onClick={toggleProfileDropdown}
                  role="button"
                  tabIndex={0}
                >
                  <div className={styles.nav_user_avatar} aria-hidden="true">
                    A
                  </div>
                  <div className={styles.nav_user_meta}>
                    <div className={styles.nav_user_name}>Admin User</div>
                    <div className={styles.nav_user_role}>
                      admin@example.com
                    </div>
                  </div>
                </div>

                {isProfileOpen && (
                  <div className={styles.profile_dropdown}>
                    <div className={styles.profile_dropdown_header}>
                      <div className={styles.profile_dropdown_name}>
                        Admin User
                      </div>
                      <div className={styles.profile_dropdown_email}>
                        admin@example.com
                      </div>
                    </div>
                    <div className={styles.profile_dropdown_body}>
                      <button className={styles.profile_dropdown_item}>
                        <FontAwesomeIcon icon={faUser} />
                        <span>Your Profile</span>
                      </button>
                    </div>
                    <div className={styles.profile_dropdown_footer}>
                      <a
                        href="/admin/logout"
                        className={styles.profile_dropdown_signout}
                      >
                        <FontAwesomeIcon icon={faArrowRightFromBracket} />
                        <span>Sign out</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Navbar>
        </div>
      </header>

      {sidebarLoading && (
        <div className={styles.loaderOverlay}>
          <img
            src={gearsGif}
            alt="Loading"
            style={{ width: 30, height: 30 }}
          />
        </div>
      )}
      <div
        className={`${styles.sidebar_container} ${isSidebarOpen ? styles.open : styles.closed
          }`}
      >
        <div className={styles.sidebar_navigation}>
          {!sidebarLoading && menuSections.length === 0 ? (
            <div style={{ padding: 12, fontSize: 12, opacity: 0.8 }}>
              {sidebarError || "No menu items"}
            </div>
          ) : (
            menuSections.map((section, index) => (
              <div key={index}>
                <span className={styles.menu_title}>{section.title}</span>
                {(section.items || []).map((item, idx) => {
                  const isDynamicKcOrForumItem = item.type === "kc" || item.type === "forum";
                  const dropdown = Array.isArray(item.dropdown) ? item.dropdown : [];
                  const hasDropdown = isDynamicKcOrForumItem || dropdown.length > 0;

                  const parentHasActiveChild =
                    !isDynamicKcOrForumItem &&
                    dropdown.some((dropdownItem) => location.pathname === dropdownItem.link);

                  const isKcRoute =
                    location.pathname.startsWith("/admin/topics") ||
                    location.pathname.startsWith("/admin/topic-entries") ||
                    location.pathname.startsWith("/admin/community-chat");

                  const isDynamicKcOrForumParent =
                    isDynamicKcOrForumItem &&
                    isKcRoute &&
                    !!activeKcSubCategoryId &&
                    (activeContext === "forum" ? item.type === "forum" : item.type === "kc");

                  const isParentActive =
                    openDropdown === item.id ||
                    parentHasActiveChild ||
                    isDynamicKcOrForumParent;

                  const showDropdownContent = isSidebarOpen
                    ? openDropdown === item.id
                    : true;

                  const itemIcon = ICON_MAP[item.icon] || faBookOpen;

                  return (
                    <div key={idx}>
                      {hasDropdown ? (
                        <div className={styles.dropdown_section}>
                          <div
                            className={`${styles.nav_item} ${isParentActive ? styles.active : ""
                              }`}
                            onClick={() => toggleDropdown(item.id)}
                            title={!isSidebarOpen ? item.label : undefined}
                          >
                            <FontAwesomeIcon icon={itemIcon} />
                            <span>{item.label}</span>
                            {openDropdown === item.id ? (
                              <FaAngleDown className={styles.angle} />
                            ) : (
                              <FaAngleRight className={styles.angle} />
                            )}
                          </div>
                          {showDropdownContent && (
                            <div className={styles.dropdown_content}>
                              {isDynamicKcOrForumItem ? (
                                <div style={{ padding: "10px", fontSize: 12 }}>
                                  {(() => {
                                    const isCommunity = item.type === "forum";
                                    const subCategories = isCommunity ? forumSubCategories : kcSubCategories;
                                    const mainCategoryId = isCommunity ? forumMainCategoryId : kcMainCategoryId;

                                    if (sidebarError) {
                                      return <div style={{ color: "red" }}>{sidebarError}</div>;
                                    }

                                    if (!mainCategoryId || subCategories.length === 0) {
                                      return (
                                        <div style={{ opacity: 0.8 }}>
                                          No sub categories found
                                        </div>
                                      );
                                    }

                                    return (
                                      <div style={{ margin: 0 }}>
                                        {/* Direct link to Category Dashboard */}
                                        <a
                                          href={`/admin/category/${mainCategoryId}`}
                                          className={
                                            location.pathname.startsWith(`/admin/category/${mainCategoryId}`)
                                              ? `${styles.active} ${styles.dropdown_active}`
                                              : ""
                                          }
                                          title={!isSidebarOpen ? "View Dashboard" : undefined}
                                          style={{ 
                                            display: "block", 
                                            padding: "8px 12px",
                                            marginBottom: "4px",
                                            fontWeight: 600,
                                            borderBottom: "1px solid #e0e0e0"
                                          }}
                                        >
                                          📊 Dashboard View
                                        </a>
                                        {/* Original subcategory links for backward compatibility */}
                                        {subCategories.map((s) => {
                                          const href = isCommunity
                                            ? `/admin/topics?mainCategoryId=${mainCategoryId}&subCategoryId=${s._id}&context=forum`
                                            : `/admin/topics?mainCategoryId=${mainCategoryId}&subCategoryId=${s._id}`;

                                          const isActiveDynamic =
                                            activeKcSubCategoryId === s._id &&
                                            (isCommunity
                                              ? activeContext === "forum"
                                              : activeContext !== "forum");

                                          return (
                                            <a
                                              key={s._id}
                                              href={href}
                                              className={
                                                isActiveDynamic
                                                  ? `${styles.active} ${styles.dropdown_active}`
                                                  : ""
                                              }
                                              title={!isSidebarOpen ? s.name : undefined}
                                            >
                                              {s.name}
                                            </a>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                dropdown.map((dropdownItem, i) => (
                                  <a
                                    key={i}
                                    href={dropdownItem.link}
                                    className={
                                      location.pathname === dropdownItem.link
                                        ? `${styles.active} ${styles.dropdown_active}`
                                        : ""
                                    }
                                    title={!isSidebarOpen ? dropdownItem.text : undefined}
                                  >
                                    {dropdownItem.text}
                                  </a>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <a
                          href={item.link}
                          className={
                            location.pathname === item.link
                              ? `${styles.nav_link} ${styles.active}`
                              : styles.nav_link
                          }
                          title={!isSidebarOpen ? item.label : undefined}
                        >
                          <div className={styles.nav_item}>
                            <FontAwesomeIcon icon={itemIcon} />
                            <span>{item.label}</span>
                          </div>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}

export default Dashboard;
